/**
 * Groups a flat line_items array into sections based on `is_section_header` markers.
 *
 * Items before any header go into a pseudo section with `header: null` (Ungrouped),
 * which is only emitted if it has at least one item. This keeps backward compatibility
 * with older quotes / change orders that never used sections.
 *
 * @param {Array} lineItems - flat array. Items with `is_section_header === true` are section dividers.
 * @returns {Array<{header: object|null, items: object[], subtotal: number}>}
 */
export function groupLineItemsBySections(lineItems) {
  const groups = [];
  let current = { header: null, items: [] };

  (lineItems || []).forEach((item) => {
    if (item?.is_section_header) {
      // flush current if it has items (even if it's the ungrouped pseudo-group)
      if (current.items.length > 0 || current.header) {
        groups.push(current);
      }
      current = { header: item, items: [] };
    } else {
      current.items.push(item);
    }
  });
  // flush last
  if (current.items.length > 0 || current.header) {
    groups.push(current);
  }

  // compute subtotals per section, honoring section lump sum + section/item optional.
  return groups.map((g) => ({
    ...g,
    subtotal: sectionSubtotal(g),
  }));
}

/**
 * Effective price of one grouped section (header + its items), applying:
 *  - a declined optional section (header.optional && header.accepted === false) => 0
 *  - a section lump sum (header.section_lump_sum) => that flat amount
 *  - otherwise the sum of its items, skipping declined optional items.
 */
export function sectionSubtotal(group) {
  const { header, items } = group;
  if (header && header.optional && header.accepted === false) return 0;
  if (header && header.section_lump_sum != null && header.section_lump_sum !== "") {
    return Number(header.section_lump_sum) || 0;
  }
  return (items || []).reduce((sum, it) => {
    if (it.optional && it.accepted === false) return sum;
    return sum + (it.total || 0);
  }, 0);
}

/** Section-aware subtotal for a flat line_items array (single source of truth). */
export function computeSubtotal(lineItems) {
  return groupLineItemsBySections(lineItems).reduce((sum, g) => sum + g.subtotal, 0);
}

/**
 * Returns true if any section header exists in the list (used to decide whether to
 * render subtotals per group in the customer view).
 */
export function hasSections(lineItems) {
  return (lineItems || []).some((i) => i?.is_section_header);
}

/** Flatten grouped sections back into a single line_items array (header then its items). */
function flattenGroups(groups) {
  const out = [];
  groups.forEach((g) => {
    if (g.header) out.push(g.header);
    (g.items || []).forEach((it) => out.push(it));
  });
  return out;
}

/**
 * Move a whole section (its header + all its items) up or down relative to the
 * other sections. The ungrouped pseudo-section (items before any header) stays
 * pinned at the top and is never reordered.
 * @param {Array} lineItems
 * @param {string} headerId - id of the section header to move
 * @param {"up"|"down"} dir
 */
export function moveSection(lineItems, headerId, dir) {
  const groups = groupLineItemsBySections(lineItems);
  const headerIdxs = groups.map((g, i) => (g.header ? i : -1)).filter((i) => i >= 0);
  const pos = groups.findIndex((g) => g.header && g.header.id === headerId);
  if (pos < 0) return lineItems;
  const among = headerIdxs.indexOf(pos);
  const target = among + (dir === "up" ? -1 : 1);
  if (target < 0 || target >= headerIdxs.length) return lineItems;
  const swapPos = headerIdxs[target];
  const next = [...groups];
  [next[pos], next[swapPos]] = [next[swapPos], next[pos]];
  return flattenGroups(next);
}

/**
 * Move a single line item up or down within its own section (won't cross into
 * another section).
 * @param {Array} lineItems
 * @param {string} itemId
 * @param {"up"|"down"} dir
 */
export function moveItem(lineItems, itemId, dir) {
  const groups = groupLineItemsBySections(lineItems);
  for (const g of groups) {
    const idx = g.items.findIndex((it) => it.id === itemId);
    if (idx >= 0) {
      const target = idx + (dir === "up" ? -1 : 1);
      if (target < 0 || target >= g.items.length) return lineItems;
      const items = [...g.items];
      [items[idx], items[target]] = [items[target], items[idx]];
      g.items = items;
      return flattenGroups(groups);
    }
  }
  return lineItems;
}
